import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

// Bumped key so old cart shape (no variant support) is discarded on upgrade.
const KEY = "twr_cart_v2";
const DATES_KEY = "twr_dates_v1";

// Composite key so a Small chair and a Large chair coexist as two lines.
const lineKey = (item_id, variant_id) => `${item_id}::${variant_id || ""}`;

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(KEY);
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });

    // Event dates chosen by the client (carried from item page into checkout).
    const [dates, setDatesState] = useState(() => {
        try {
            const raw = localStorage.getItem(DATES_KEY);
            return raw ? JSON.parse(raw) : { start_date: "", end_date: "" };
        } catch { return { start_date: "", end_date: "" }; }
    });

    useEffect(() => {
        try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
    }, [items]);

    useEffect(() => {
        try { localStorage.setItem(DATES_KEY, JSON.stringify(dates)); } catch {}
    }, [dates]);

    const setDates = (patch) => setDatesState((cur) => ({ ...cur, ...patch }));

    const add = (item, qty = 1, variant = null) => {
        const key = lineKey(item.id, variant?.id);
        setItems((cur) => {
            const idx = cur.findIndex((x) => x.key === key);
            if (idx >= 0) {
                const copy = [...cur];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
                return copy;
            }
            return [...cur, {
                key,
                item_id: item.id,
                item_name: variant ? `${item.name} · ${variant.size}` : item.name,
                variant_id: variant?.id || null,
                variant_size: variant?.size || null,
                unit_price: variant ? Number(variant.rental_price || 0) : Number(item.rental_price || 0),
                deposit: variant ? Number(variant.deposit || 0) : Number(item.deposit || 0),
                photo_path: item.photo_path || (item.photos && item.photos[0]) || null,
                quantity: qty,
            }];
        });
    };

    const remove = (key) => setItems((cur) => cur.filter((x) => x.key !== key));
    const update = (key, quantity) => setItems((cur) => cur.map((x) => x.key === key ? { ...x, quantity: Math.max(1, quantity) } : x));
    const clear = () => { setItems([]); setDatesState({ start_date: "", end_date: "" }); };

    const total = items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
    const depositTotal = items.reduce((s, i) => s + (i.quantity * (i.deposit || 0)), 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);

    return (
        <CartContext.Provider value={{ items, add, remove, update, clear, total, depositTotal, count, dates, setDates }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
